import { useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, ReferenceLine, LabelList,
} from "recharts";
import type { DemoAttendeeRow } from "../sql";
import type { ViewMode } from "../helpers";
import { PRIMARY, MUTED, FG1 } from "../../../lib/funnel/theme";
import { fmtN } from "../../../lib/funnel/helpers";
import { InfoButton } from "../../../lib/funnel/InfoButton";
import { SectionCardHeader } from "../../../components/ChartCard";
import type { FilterPillSpec } from "../../../components/FilterPills";

const BUCKETS: { label: string; min: number; max: number }[] = [
  { label: "Same day", min: 0, max: 0 },
  { label: "1–3 d", min: 1, max: 3 },
  { label: "4–7 d", min: 4, max: 7 },
  { label: "8–14 d", min: 8, max: 14 },
  { label: "15–30 d", min: 15, max: 30 },
  { label: "31–60 d", min: 31, max: 60 },
  { label: "61–90 d", min: 61, max: 90 },
  { label: "90+ d", min: 91, max: Infinity },
];

const MEDIAN_COLOR = PRIMARY;
const AVERAGE_COLOR = "#C75A4A";

function bucketFor(days: number): string | null {
  if (days < 0) return null;
  for (const b of BUCKETS) {
    if (days >= b.min && days <= b.max) return b.label;
  }
  return null;
}

function quantile(sorted: number[], q: number): number | null {
  if (sorted.length === 0) return null;
  const idx = (sorted.length - 1) * q;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  const a = sorted[lo];
  const b = sorted[hi];
  if (a == null || b == null) return null;
  return a + (b - a) * (idx - lo);
}

type LabelProps = {
  x?: string | number;
  y?: string | number;
  width?: string | number;
  value?: unknown;
};

const num = (v: unknown): number | null => {
  if (v == null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

// Pill-style top label so the value stays readable when reference lines and
// labels overlap. Renders a rounded rect behind the count.
function PillLabel(props: LabelProps) {
  const x = num(props.x), y = num(props.y), w = num(props.width), v = num(props.value);
  if (x == null || y == null || w == null || v == null) return null;
  if (v <= 0) return null;
  const text = fmtN(Math.round(v));
  const padX = 6;
  const charW = 6.5;
  const boxW = Math.max(20, text.length * charW + padX * 2);
  const boxH = 16;
  const cx = x + w / 2;
  const top = y - boxH - 4;
  return (
    <g>
      <rect
        x={cx - boxW / 2}
        y={top}
        width={boxW}
        height={boxH}
        rx={4}
        fill="#fff"
        stroke="rgba(0,0,0,0.12)"
      />
      <text
        x={cx}
        y={top + boxH / 2 + 0.5}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={11}
        fontWeight={600}
        fill={FG1}
      >
        {text}
      </text>
    </g>
  );
}

export function DaysToSignupHistogram({ rows, viewMode, pagePills }: { rows: DemoAttendeeRow[]; viewMode: ViewMode; pagePills?: FilterPillSpec[] }) {
  const isCompany = viewMode === "company";
  const unit = isCompany ? "companies" : "attendees";
  const stats = useMemo(() => {
    const days: number[] = [];
    for (const r of rows) {
      if (r.signup_timing_bucket === "never" || r.signup_timing_bucket === "before") continue;
      if (r.days_demo_to_signup == null) continue;
      if (r.days_demo_to_signup < 0) continue;
      days.push(r.days_demo_to_signup);
    }
    days.sort((a, b) => a - b);
    const counts = new Map<string, number>();
    for (const b of BUCKETS) counts.set(b.label, 0);
    for (const d of days) {
      const k = bucketFor(d);
      if (k) counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    const sum = days.reduce((s, x) => s + x, 0);
    return {
      total: days.length,
      median: quantile(days, 0.5),
      p75: quantile(days, 0.75),
      mean: days.length > 0 ? sum / days.length : null,
      data: BUCKETS.map((b) => ({ label: b.label, value: counts.get(b.label) ?? 0 })),
    };
  }, [rows]);

  if (stats.total === 0) {
    return (
      <section className="card" style={{ height: "100%" }}>
        <SectionCardHeader
          title={<span style={{ fontSize: 14 }}>Days from demo to sign-up</span>}
          filterPills={pagePills}
        />
        <div className="empty" style={{ padding: 24 }}>No matched signups in cohort.</div>
      </section>
    );
  }

  const yMax = Math.max(...stats.data.map((d) => d.value), 1);
  const medianBucket = stats.median != null ? bucketFor(Math.round(stats.median)) : null;
  const meanBucket = stats.mean != null ? bucketFor(Math.round(stats.mean)) : null;

  return (
    <section className="card" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <SectionCardHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            Days from demo to sign-up
            <InfoButton label="Days-to-signup">
              <p style={{ margin: 0 }}>
                Distribution of <code>days_demo_to_signup</code> for signed-up {unit} only. The "Signed up before demo"
                cohort is shown in the timing chart, not here. The median anchors forecasts; the mean (dashed orange) shows
                how heavy the long tail is.
              </p>
            </InfoButton>
          </span>
        }
        subtitle={
          <span>
            n={fmtN(stats.total)} · median{" "}
            <span style={{ color: MEDIAN_COLOR, fontWeight: 600 }}>
              {stats.median != null ? `${Math.round(stats.median)}d` : "—"}
            </span>
            {" · mean "}
            <span style={{ color: AVERAGE_COLOR, fontWeight: 600 }}>
              {stats.mean != null ? `${Math.round(stats.mean)}d` : "—"}
            </span>
            {" · p75 "}{stats.p75 != null ? `${Math.round(stats.p75)}d` : "—"}
          </span>
        }
        filterPills={pagePills}
      />
      <div className="card-body" style={{ flex: 1, minHeight: 320, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", gap: 16, alignItems: "center", justifyContent: "center", padding: "0 0 8px", fontSize: 11 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ display: "inline-block", width: 18, height: 0, borderTop: `2px dashed ${MEDIAN_COLOR}` }} />
            <span style={{ color: MEDIAN_COLOR, fontWeight: 600 }}>
              Median {stats.median != null ? `${Math.round(stats.median)}d` : "—"}
            </span>
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ display: "inline-block", width: 18, height: 0, borderTop: `2px dashed ${AVERAGE_COLOR}` }} />
            <span style={{ color: AVERAGE_COLOR, fontWeight: 600 }}>
              Mean {stats.mean != null ? `${Math.round(stats.mean)}d` : "—"}
            </span>
          </span>
        </div>
        <ResponsiveContainer width="100%" height="100%" minHeight={320}>
          <BarChart data={stats.data} margin={{ top: 36, right: 16, left: 16, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: FG1 }} />
            <YAxis tick={{ fontSize: 10, fill: MUTED }} domain={[0, yMax]} allowDecimals={false} width={32} />
            <Tooltip
              cursor={{ fill: "rgba(0,0,0,0.03)" }}
              formatter={(v) => [`${fmtN(Number(v))} ${unit}`, "Count"] as [string, string]}
            />
            {medianBucket && (
              <ReferenceLine
                x={medianBucket}
                stroke={MEDIAN_COLOR}
                strokeWidth={2}
                strokeDasharray="3 3"
              />
            )}
            {meanBucket && (
              <ReferenceLine
                x={meanBucket}
                stroke={AVERAGE_COLOR}
                strokeWidth={2}
                strokeDasharray="6 4"
              />
            )}
            <Bar dataKey="value" fill={PRIMARY} radius={[3, 3, 0, 0]} isAnimationActive={false}>
              <LabelList dataKey="value" content={PillLabel} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
